import { BoxProps, Chip, MenuItem, Select, Stack, TextField } from "@mui/material"
import React from "react"
import { useTranslation } from "react-i18next"
import CancelIcon from "@mui/icons-material/Cancel"
import { Box, Typography } from "src/UILibrary"
import { SelectDataType } from "./SelectDataType"
import { getLabel } from "src/modules/helper"

type Props = {
  sx?: BoxProps
  name?: string
  required?: boolean
  selected: (string | number)[]
  handleSelected: any
  datas: SelectDataType[]
  inputStyle?: any
  labelStyle?: any
  disabled?: boolean
  hasText?: boolean
  text?: string
  handleText?: any
  multiline?: boolean
  validationMessage?: string
  placeholder?: string
  checkValidation?: any
  padding?: any
}

const MultiSelectField = ({
  sx,
  name,
  required,
  selected,
  handleSelected,
  datas,
  inputStyle,
  labelStyle,
  disabled,
  hasText,
  text,
  handleText,
  multiline,
  validationMessage,
  placeholder,
  checkValidation,
  padding,
}: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "base" })

  const style = sx || {}

  const customStyle = {
    "& ::before": {
      borderBottomColor: "#d4d3d7 !important",
    },
  }

  const isOther = () => {
    for (let selection of selected) {
      for (const item of datas) {
        if (item.value === selection) {
          if (item.label === "その他") {
            return true
          }
        }
      }
    }

    return false
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "355px",
        ...style,
        ...customStyle,
      }}
    >
      {name && (
        <Box display="flex" sx={{ mb: "8px" }}>
          <Typography.InputFieldTitle sx={{ mr: "6px", ...labelStyle }}>
            {name}
          </Typography.InputFieldTitle>
          {required && (
            <Box
              sx={{
                px: "8px",
                borderWidth: "1px",
                borderStyle: "solid",
                borderRadius: "4px",
                borderColor: "text.border",
              }}
            >
              <Typography.InputFieldRequired sx={{ m: "auto", color: "text.border" }}>
                {t("required")}
              </Typography.InputFieldRequired>
            </Box>
          )}
        </Box>
      )}
      <Select
        multiple
        variant="outlined"
        sx={{
          width: "100%",
          ...inputStyle,
          mt: 0,
          mb: 0,
          ".MuiSelect-icon": {
            fill: "#00C298 !important",
          },
          ".MuiInputBase-input": {
            display: "flex",
            alignItems: "center",
            py: padding,
          },
          "& fieldset": { borderColor: "#F3F3F3" },
        }}
        value={selected || []}
        onChange={(event) => handleSelected(event.target.value)}
        className={selected == null ? "gray" : ""}
        disabled={disabled}
        renderValue={(selected) => {
          if (checkValidation) {
            checkValidation()
          }
          if (selected[0] == 0 && selected.length == 1) {
            return (
              <Typography.InputFieldTitle className="gray">
                {placeholder}
              </Typography.InputFieldTitle>
            )
          } else
            return (
              <Stack gap={1} direction="row" flexWrap="wrap">
                {selected.map((value) => {
                  if (value == 0) return
                  return (
                    <Chip
                      key={value}
                      label={getLabel(datas, value as number)}
                      onDelete={() => handleSelected(selected.filter((item) => item !== value))}
                      deleteIcon={
                        <CancelIcon
                          sx={{ backgroundColor: "#C5E7FF" }}
                          onMouseDown={(event) => event.stopPropagation()}
                        />
                      }
                      sx={{
                        backgroundColor: "#C5E7FF",
                        width: "auto",
                        height: "23px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        margin: "0px",
                        fontFamily: "Zen Kaku Gothic New !important",
                      }}
                    />
                  )
                })}
              </Stack>
            )
        }}
      >
        {placeholder && (
          <MenuItem
            value={0}
            key={0}
            disabled
            sx={{ display: "none", fontFamily: "Zen Kaku Gothic New !important", fontSize: "15px" }}
          >
            {placeholder}
          </MenuItem>
        )}
        {datas.map((data, index) => {
          return (
            <MenuItem
              value={data.value}
              key={index}
              sx={{ fontFamily: "Zen Kaku Gothic New !important", fontSize: "14px" }}
            >
              {data.label}
            </MenuItem>
          )
        })}
      </Select>
      {hasText && isOther() && (
        <TextField
          variant="standard"
          sx={{
            width: "200px",
            fontFamily: "Noto Sans JP !important",
            mt: "10px",
          }}
          multiline={multiline}
          minRows={multiline ? 4 : 1}
          disabled={disabled}
          value={text}
          onChange={(event) => handleText!(event.target.value)}
        />
      )}
      {validationMessage && (
        <Box sx={{ height: "20px", pt: "5px" }}>
          <Typography.ValidationText>{validationMessage}</Typography.ValidationText>
        </Box>
      )}
    </Box>
  )
}

export default MultiSelectField
